import React, { useEffect } from 'react';
import '../styles/App.css';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';

const images = [
  '/ad-images/ad-image-1.png',
  '/ad-images/ad-image-2.png',
  '/ad-images/ad-image-3.png',
  '/ad-images/ad-image-4.png',
  '/ad-images/ad-image-5.png',
  '/ad-images/ad-image-1.png',
  '/ad-images/ad-image-2.png',
  '/ad-images/ad-image-3.png',
  '/ad-images/ad-image-4.png',
  '/ad-images/ad-image-5.png'
];

const features = [
  {
    number: '01',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
      </svg>
    ),
    titleKey: 'home.reason-1.heading',
    descKey: 'home.reason-1.description'
  },
  {
    number: '02',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
      </svg>
    ),
    titleKey: 'home.reason-2.heading',
    descKey: 'home.reason-2.description'
  },
  {
    number: '03',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 3l1.912 5.813a2 2 0 001.272 1.272L21 12l-5.813 1.912a2 2 0 00-1.272 1.272L12 21l-1.912-5.813a2 2 0 00-1.272-1.272L3 12l5.813-1.912a2 2 0 001.272-1.272L12 3z"></path>
      </svg>
    ),
    titleKey: 'home.reason-3.heading',
    descKey: 'home.reason-3.description'
  },
  {
    number: '04',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="18" cy="5" r="3"></circle>
        <circle cx="6" cy="12" r="3"></circle>
        <circle cx="18" cy="19" r="3"></circle>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
      </svg>
    ),
    titleKey: 'home.reason-4.heading',
    descKey: 'home.reason-4.description'
  }
];

const Home = () => {
  const { t, i18n } = useTranslation();
  const { loginWithRedirect } = useAuth0();

  // Always use Canadian English
  useEffect(() => {
    i18n.changeLanguage('en-CA');
  }, [i18n]);

  const ImageSlider = ({images, duration = 5}) => {
    const count = images.length;
    const keyframes = `@keyframes slide {
      0% { transform: translateX(0); }
      100% { transform: translateX(-${100/count}%) }
    }`;

    return (
      <div className="image-slider" style={{animationDuration: `${duration}s`}}>
        <style>{keyframes}</style>
        {images.map((img, index) =>
          <div className="image-container" key={index}>
            <img src={img} alt=""/>
          </div>
        )}
        {images.map((img, index) =>
          <div className="image-container" key={index+count}>
            <img src={img} alt=""/>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="modern-homepage">
      <main>
        {/* Hero Section */}
        <section className="hero-section">
          {/* Background Elements */}
          <div className="hero-bg-gradient"></div>
          <div className="hero-blob hero-blob-1"></div>
          <div className="hero-blob hero-blob-2"></div>

          <div className="hero-content">
            <div className="hero-text-container">
              {/* Main Heading */}
              <h1 className="hero-heading">
                {t('home.hero.heading')}{' '}
                <span className="hero-gradient-text">{t('home.hero.tagline')}</span>
              </h1>

              {/* Subheading */}
              <p className="hero-description">
                {t('home.hero.description')}
              </p>

              {/* CTA Buttons */}
              <div className="hero-cta-buttons">
                <button
                  className="hero-btn-primary"
                  onClick={() => loginWithRedirect({ redirectUri: `${window.location.origin}/dashboard` })}
                >
                  {t('hero.sign-up-today')}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
                <button className="hero-btn-outline">
                  {t('hero.watch-demo')}
                </button>
              </div>
            </div>

            {/* Product Showcase */}
            <div className="hero-showcase">
              <div className="slider-container">
                <ImageSlider images={images} duration={20} />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="why-choose-us" className="features-section">
          <div className="features-container">
            {/* Section Header */}
            <div className="features-header">
              <h2 className="features-title">
                {t('home.reasons.heading')}
              </h2>
              <p className="features-subtitle">
                {t('home.reasons.description-1')}
                <span className="text-purple">{t('home.reasons.product')}</span>
                {t('home.reasons.description-2')}
              </p>
            </div>

            {/* Features Grid */}
            <div className="features-grid">
              {features.map((feature) => (
                <div key={feature.number} className="feature-card">
                  {/* Icon */}
                  <div className="feature-icon">
                    {feature.icon}
                  </div>

                  {/* Number */}
                  <div className="feature-number">{feature.number}</div>

                  {/* Content */}
                  <h3 className="feature-title">{t(feature.titleKey)}</h3>
                  <p className="feature-description">{t(feature.descKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="pricing-section">
          <div className="pricing-container">
            {/* Section Header */}
            <div className="pricing-header">
              <h2 className="pricing-title">{t('home.pricing.heading')}</h2>
              <p className="pricing-subtitle">{t('home.pricing.description')}</p>
            </div>

            {/* Pricing Cards */}
            <div className="pricing-cards pricing-cards-single">
              {/* Standard Plan */}
              <div className="pricing-card-modern pricing-card-popular">
                <div className="pricing-card-content">
                  <h4 className="plan-name">{t('home.pricing.standard-plan.title')}</h4>
                  <div className="plan-price">
                    <span className="currency">{t('home.pricing.currency')}</span>
                    <span className="amount">{t('home.pricing.standard-plan.price')}</span>
                    <span className="period">{t('home.pricing.standard-plan.per')}{t('home.pricing.standard-plan.unit')}</span>
                  </div>
                  <ul className="plan-features">
                    <li>
                      <span className="check-icon">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </span>
                      {t('home.pricing.standard-plan.no-of-locations')}
                    </li>
                    <li>
                      <span className="check-icon">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </span>
                      {t('home.pricing.standard-plan.no-of-ads')}
                    </li>
                    <li>
                      <span className="check-icon">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </span>
                      {t('home.pricing.standard-plan.frequency-of-updates')}
                    </li>
                    <li>
                      <span className="check-icon">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </span>
                      {t('home.pricing.standard-plan.distribution')}
                    </li>
                    <li>
                      <span className="check-icon">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </span>
                      {t('home.pricing.standard-plan.hours-of-support')}
                    </li>
                  </ul>
                  <button
                    className="plan-btn plan-btn-popular"
                    onClick={() => loginWithRedirect({ redirectUri: `${window.location.origin}/dashboard` })}
                  >
                    {t('hero.get-started')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="modern-footer">
          <div className="footer-container">
            <div className="footer-grid">
              {/* Brand */}
              <div className="footer-brand">
                <div className="footer-logo">
                  <img src='/adgro-logo-color.png' alt='adgro' className="footer-logo-img" />
                </div>
                <p className="footer-description">
                  {t('footer-links.description')}
                </p>
              </div>

              {/* Quick Links */}
              <div className="footer-links">
                <h4>{t('footer-links.quick-links')}</h4>
                <ul>
                  <li><a href="/">{t('header.nav.home')}</a></li>
                  <li><a href="#why-choose-us">{t('header.nav.features')}</a></li>
                  <li><a href="#pricing">{t('header.nav.pricing')}</a></li>
                  <li><a href="#signup-form">{t('header.nav.contact')}</a></li>
                </ul>
              </div>

              {/* Legal */}
              <div className="footer-links">
                <h4>{t('footer-links.legal')}</h4>
                <ul>
                  <li><a href="/terms">{t('footer.terms')}</a></li>
                  <li><a href="/privacy">{t('footer.privacy')}</a></li>
                </ul>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="footer-bottom">
              {t('footer.copyright')}
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default Home;
